import React from 'react';
import { Box, IconButton, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ReactDatePicker from '../../react-datepicker-compat';
import 'react-datepicker/dist/react-datepicker.css';
import { ReactComponent as VisibilityIcon } from '../../static/images/svg/ic_round-visibility.svg';
import { ReactComponent as VisibilityOffIcon } from '../../static/images/svg/ic_round-visibility-off.svg';

/** 我的账户页下拉选项 */
export type MyAccountSelectOption = {
    /** 选项值 */
    value: string;
    /** 选项文案 */
    label: string;
};

/** 我的账户页专用输入框类型 */
type MyAccountInputType = 'INPUT' | 'SELECT' | 'DATE' | 'PASSWORD';

/** 我的账户页专用输入框参数 */
type MyAccountFormInputProps = {
    /** 左侧标签文案 */
    label: string;
    /** 当前值 */
    value: string;
    /** 输入类型 */
    inputType?: MyAccountInputType;
    /** 下拉选项 */
    options?: MyAccountSelectOption[];
    /** 日期最大值 */
    maxDate?: Date;
    /** 是否禁用 */
    disabled?: boolean;
    /** 值变化回调 */
    onChange: (value: string) => void;
};

/**
 * 我的账户页专用灰底输入框（左标签右输入、无边框）
 * 仅用于 my-account 页面，避免影响全局公共输入组件。
 */
export default function MyAccountFormInput(props: Readonly<MyAccountFormInputProps>) {
    // 多语言函数，用于密码显隐按钮的无障碍文案
    const { t } = useTranslation();
    // 输入类型，默认普通输入
    const { label, value, inputType = 'INPUT', options = [], maxDate, disabled = false, onChange } = props;
    // 普通输入框的 DOM 引用，用于点击灰色输入区域时主动聚焦
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    // 密码是否以明文显示
    const [showPassword, setShowPassword] = React.useState(false);
    // 日期值：字符串格式转 Date，供日期选择器使用
    const dateValue = React.useMemo(() => {
        if (!value) return null;
        const parsedDate = moment(value, moment.HTML5_FMT.DATETIME_LOCAL_MS, true);
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    }, [value]);

    /**
     * 处理日期变化并回写为接口兼容格式（带毫秒）
     * @param nextDate - 选择后的日期
     */
    const handleDateChange = (nextDate: Date | null) => {
        const formattedDate = nextDate ? moment(nextDate).format(moment.HTML5_FMT.DATETIME_LOCAL_MS) : '';
        onChange(formattedDate);
    };

    /**
     * 点击灰色字段区域时聚焦输入控件，点击左侧标签时保持原有行为。
     * @param event - 字段容器的鼠标按下事件
     */
    const handleFieldInteraction = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (target.closest('.my-account-inline-field__label') || target.closest('[data-password-toggle="true"]')) {
            return;
        }
        inputRef.current?.focus();
    };

    return (
        <Box
            className='my-account-inline-field'
            onClick={handleFieldInteraction}
            onMouseDown={handleFieldInteraction}
        >
            <Box className='my-account-inline-field__label'>
                <Typography
                    variant='body1'
                    component='div'
                    className='my-account-inline-field__label-text'
                    title={label}
                    sx={{ color: 'var(--text-dark)' }}
                >
                    {label}
                </Typography>
            </Box>
            <Box className='my-account-inline-field__control'>
                {inputType === 'DATE' ? (
                    <ReactDatePicker
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        selected={dateValue}
                        onChange={handleDateChange}
                        dateFormat='yyyy/MM/dd'
                        maxDate={maxDate}
                        disabled={disabled}
                        customInput={
                            <TextField
                                fullWidth
                                size='small'
                                value={value}
                                inputRef={inputRef}
                                inputProps={{ readOnly: true, title: value || '' }}
                                variant='outlined'
                            />
                        }
                    />
                ) : (
                    <TextField
                        fullWidth
                        size='small'
                        select={inputType === 'SELECT'}
                        type={inputType === 'PASSWORD' && !showPassword ? 'password' : 'text'}
                        value={value}
                        disabled={disabled}
                        onChange={(event) => onChange(event.target.value)}
                        inputRef={inputRef}
                        inputProps={{ title: value || '' }}
                        InputProps={inputType === 'PASSWORD' ? {
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <IconButton
                                        aria-label={showPassword ? t('my_account_password_hide') : t('my_account_password_show')}
                                        className='my-account-inline-field__password-toggle'
                                        data-password-toggle='true'
                                        edge='end'
                                        onClick={() => setShowPassword((current) => !current)}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                    >
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        } : undefined}
                        variant='outlined'
                    >
                        {inputType === 'SELECT'
                            ? options.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))
                            : null}
                    </TextField>
                )}
            </Box>
        </Box>
    );
}
